import React, { useMemo } from "react"
import { useForm, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, message, Alert, Typography } from "@mimber/ui-web"
import {
  Form,
  SelectField,
  SwitchField,
  CheckboxField,
  DatePickerField,
} from "@mimber/forms"
import dayjs from "dayjs"
import {
  useAssignMemberTier,
  useMembershipTiers,
  useDuesSettings,
  useTierChangePreview,
} from "@mimber/client/mimber-organization"
import { formatCurrency } from "@/utils"
import {
  assignTierToMemberFormSchema,
  type AssignTierToMemberFormData,
} from "./AssignTierToMemberForm.schema"
import type { AssignTierToMemberFormProps } from "./AssignTierToMemberForm.types"
import {
  TIER_CHANGE_POLICY_LABELS,
  TIER_CHANGE_POLICY_OVERRIDE_OPTIONS,
} from "./AssignTierToMemberForm.utils"

const { Text } = Typography

export const AssignTierToMemberForm: React.FunctionComponent<
  AssignTierToMemberFormProps
> = ({ memberUuid, currentTierUuid, onSuccess, onCancel }) => {
  const isTierChange = !!currentTierUuid

  const form = useForm<AssignTierToMemberFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(assignTierToMemberFormSchema) as any,
    mode: "onBlur",
    defaultValues: {
      tierId: currentTierUuid ?? undefined,
      isTierPermanent: false,
      voidExistingOpenInvoices: true,
      tierChangePolicyOverride: null,
    },
  })

  const { control } = form

  const tierId = useWatch({ control, name: "tierId" })
  const isTierPermanent = useWatch({ control, name: "isTierPermanent" })
  const tierChangePolicyOverride = useWatch({
    control,
    name: "tierChangePolicyOverride",
  })

  const { data: tiersResponse, isLoading: tiersLoading } = useMembershipTiers({
    limit: 100,
  })

  const { data: duesSettingsResponse } = useDuesSettings()

  const isPreviewableChange =
    isTierChange && !!tierId && tierId !== currentTierUuid

  const { data: previewResponse } = useTierChangePreview(memberUuid, {
    tierId: isPreviewableChange ? (tierId ?? "") : "",
    isTierPermanent,
    tierChangePolicyOverride,
  })
  const preview = previewResponse?.data

  const tierOptions = useMemo(() => {
    if (!tiersResponse?.data) return []
    return tiersResponse.data
      .filter((tier) => tier.isActive)
      .map((tier) => ({
        label: tier.name,
        value: tier.uuid,
      }))
  }, [tiersResponse])

  const assignTierMutation = useAssignMemberTier(memberUuid, {
    onSuccess: () => {
      message.success("Membership tier assigned successfully")
      onSuccess?.()
    },
    onError: (error) => {
      message.error(`Failed to assign tier: ${error.message}`)
    },
  })

  const onSubmit = (data: AssignTierToMemberFormData) => {
    assignTierMutation.mutate(data)
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={assignTierMutation.isPending}
      layout="vertical"
    >
      <SelectField
        name="tierId"
        label="Membership Tier"
        placeholder="Select a tier"
        options={tierOptions}
        loading={tiersLoading}
        showSearch
        filterOption={(input, option) =>
          String(option?.label ?? "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        required
      />

      <SwitchField
        name="isTierPermanent"
        label="Permanent Assignment"
        description="Override tier expiration — this member will keep this tier indefinitely"
      />

      <CheckboxField
        name="voidExistingOpenInvoices"
        help="Void any open membership dues invoice from the previous tier. Uncheck to leave it open and create a new invoice anyway."
      >
        Void existing open dues invoice
      </CheckboxField>

      {!isTierChange && (
        <DatePickerField
          name="dueAt"
          label="First Billing Date"
          help="Defaults to today if left blank. Anchors both the first invoice and the recurring billing cycle."
        />
      )}

      {isTierChange && (
        <>
          {duesSettingsResponse?.data && (
            <Text
              type="secondary"
              style={{ display: "block", marginBottom: 16 }}
            >
              Your organization&apos;s default tier-change policy is{" "}
              <strong>
                {
                  TIER_CHANGE_POLICY_LABELS[
                    duesSettingsResponse.data.tierChangePolicy
                  ]
                }
              </strong>
              .
            </Text>
          )}

          <SelectField
            name="tierChangePolicyOverride"
            label="Billing for this change"
            placeholder="Use organization default"
            allowClear
            options={TIER_CHANGE_POLICY_OVERRIDE_OPTIONS}
          />

          {isPreviewableChange && preview && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                preview.willGenerateInvoiceNow
                  ? `This will generate a ${formatCurrency(preview.previewAmount)} invoice due ${
                      preview.previewDueDate
                        ? dayjs(preview.previewDueDate).format("MMM D, YYYY")
                        : "today"
                    }.`
                  : `No invoice will be generated now — this member's next invoice is due ${
                      preview.currentCycleEndsAt
                        ? dayjs(preview.currentCycleEndsAt).format(
                            "MMM D, YYYY"
                          )
                        : "at the next renewal"
                    }.`
              }
            />
          )}
        </>
      )}

      <Space
        style={{ width: "100%", justifyContent: "flex-end", marginTop: 16 }}
      >
        <Button onClick={onCancel} disabled={assignTierMutation.isPending}>
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={assignTierMutation.isPending}
        >
          Assign Tier
        </Button>
      </Space>
    </Form>
  )
}
